import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderDetailsCard } from '@components/OrderProcessing/OrderDetailsCard';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import {
  faChevronUp,
  faChevronDown,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  InvocieCrumbSteps,
  Length,
  OrderProcessingMasterData,
  PickupCrumbSteps,
  responseTexts,
  WeightType,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { InvoiceDetailsResponse, OrderServiceNotes } from '@helpers/Models';
import { PdfDownloadService } from '@services/PdfDownloadService';
import { formatDate } from '@services/OrderCreationService';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const InvoiceDetails: React.FC = () => {
  const { state } = useLocation();

  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const { showConfirmationToast } = UseConfirmationToast();
  const [invoiceDetails, setInvoiceDetails] =
    useState<InvoiceDetailsResponse>();
  const { showToastMessage } = useToast();
  const [extraStorageFees, setExtraStorageFees] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [showRefund, setShowRefund] = useState<boolean>(false);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);

  const [showPickedUpNotification, setShowPickedUpNotification] =
    useState<boolean>(false);
  const [finalPriceToBePaid, setFinalPriceToBePaid] = useState(0);

  /**
   * Fetches the invoice details when the component mounts.
   *
   * This effect runs only once on the initial render (componentDidMount behavior)
   * because the dependency array is empty.
   *
   * It calls the `fetchInvoiceDetails` function to retrieve invoice data
   * from the server or API and populate the component state.
   */
  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  /**
   * Fetches invoice details from the server using the invoice ID from state.
   *
   * - Sends a POST request to the `ORDER_INVOICE_VIEW` endpoint.
   * - On success:
   *   - Updates invoice details using `setInvoiceDetails`.
   *   - Extracts and sets the tax rate from the response (`invoiceInfo.taxRate`), defaulting to 0 if not found.
   * - On failure:
   *   - Shows a toast message indicating a fetch error.
   *
   * @async
   * @function fetchInvocieDetails
   * @returns {Promise<void>} A promise that resolves when invoice details are fetched and stored.
   */
  const fetchInvoiceDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_INVOICE_VIEW, {
        id: state.invoiceId,
      });

      setInvoiceDetails(response.data.data);
      setExtraStorageFees(response.data.data.invoiceInfo.storageCost || 0);
      setDiscount(response.data.data.invoiceInfo.discountAmount || 0);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the expansion state of an accordion section.
   *
   * Maintains an array (`openAccordions`) where each entry is the index of an
   * accordion panel that is currently open. If the provided `index` is already
   * present in the array, it is removed (panel closes). Otherwise, the `index`
   * is added (panel opens).
   *
   * @function toggleAccordion
   * @param {number} index - The zero‑based index of the accordion panel to
   *                         toggle.
   * @returns {void} This function updates state via `setOpenAccordions` and
   *                 does not return a value.
   */
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Calculates the total deposit amount across all species in the invoice.
   *
   * - Iterates over each species in `invoiceDetails.orderInfo.species`.
   * - For each species, it sums the `depositAmount` of all associated services.
   * - If a service does not have a `depositAmount`, it defaults to 0.
   * - If `invoiceDetails` or `species` are undefined, the result defaults to 0.
   *
   * @constant
   * @type {number}
   *
   */
  const totalDeposits =
    invoiceDetails?.orderInfo.species.reduce((depositSum, species): number => {
      const speciesDeposit = species.services?.reduce(
        (sum, service) => sum + (service.depositAmount || 0),
        0,
      );
      return depositSum + speciesDeposit;
    }, 0) || 0;

  /**
   * Updates the final amount to be paid by the customer and determines if a refund is required.
   *
   * This effect runs whenever any of the following dependencies change:
   * - extraStorageFees: Additional fees for storage that need to be added to the balance.
   * - discount: Discount amount that needs to be subtracted from the balance.
   * - invoiceDetails: Contains invoice information including the initial balance amount.
   *
   * The effect calculates the updated final price as:
   *   finalPriceToBePaid = balanceAmount + extraStorageFees - discount
   *
   * After calculating:
   * - Updates the state `finalPriceToBePaid` with the computed value.
   * - Sets `showRefund` to true if the final price is negative (indicating a refund to the customer).
   *
   * Dependencies:
   * - extraStorageFees: number
   * - discount: number
   * - invoiceDetails: object containing invoiceInfo with balanceAmount
   */
  useEffect(() => {
    if (!invoiceDetails?.invoiceInfo) return;

    const balanceAmount = invoiceDetails.invoiceInfo.balanceAmount || 0;

    const updatedFinalPrice =
      balanceAmount + (extraStorageFees || 0) - (discount || 0);

    setFinalPriceToBePaid(updatedFinalPrice);
    setShowRefund(updatedFinalPrice < 0);
  }, [extraStorageFees, discount, invoiceDetails]);

  /**
   * Sends a payment confirmation request to mark the invoice as paid.
   *
   * - Makes a POST request to the `MARK_AS_PAID` API endpoint.
   * - Sends the following data:
   *   - `id`: The invoice or order ID from state.
   *   - `storageCost`: Any extra storage fees.
   *   - `discountAmount`: The applied discount.
   * - On success:
   *   - Sets `showReadyForUpNotification` to `true`, likely to trigger UI feedback or a status update.
   * - On failure:
   *   - Displays a toast message with a generic fetch error.
   *
   * @async
   * @function handleConfirmPayment
   * @returns {Promise<void>} A promise that resolves when the request completes.
   *
   */
  const handleConfirmPayment = async (): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.MARK_AS_PAID, {
        id: state.invoiceId,
        storageCost: extraStorageFees,
        discountAmount: discount,
      });
      fetchInvoiceDetails();
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Sends a request to mark the current invoice as picked up.
   *
   * This function posts the invoice ID stored in state to the `MARK_AS_PICKEDUP` API endpoint.
   * On success, it triggers a UI notification by setting `setShowPickedUpNotification` to `true`.
   * On failure, it displays a toast message with a generic fetch error.
   *
   * @returns {Promise<void>} A promise that resolves once the operation completes.
   */
  const handleConfirmPickedUp = async (): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.MARK_AS_PICKEDUP, {
        id: state.invoiceId,
      });
      fetchInvoiceDetails();
      setShowPickedUpNotification(true);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles the download of invoice PDF
   * Uses the PdfDownloadService to download the PDF for the selected invoice
   */
  const handlePdfDownload = async (): Promise<void> => {
    if (!state.invoiceId) {
      showToastMessage(errorMessage.INVOICE_NOT_FOUND, 'error');
      return;
    }

    try {
      await PdfDownloadService.downloadInvoicePdf(
        state.invoiceId,
        `invoice-${invoiceDetails?.orderInfo.orderNumber || state.invoiceId}.pdf`,
        () => {
          showToastMessage(responseTexts.PDF_DOWNLOAD_SUCCESS, 'success');
        },
        (error: Error) => {
          console.error('PDF download error:', error);
          showToastMessage(errorMessage.ERROR_DOWNLOADING_PDF, 'error');
        },
      );
    } catch (error) {
      console.error('PDF download error:', error);
      showToastMessage(errorMessage.ERROR_DOWNLOADING_PDF, 'error');
    }
  };

  // Order Info Details for passing it to next screen
  const OrderInfoDetails = {
    orderId: invoiceDetails?.orderInfo.orderId,
    orderNumber: invoiceDetails?.orderInfo.orderNumber,
    customerName: invoiceDetails?.orderInfo.customerName,
    roles: invoiceDetails?.orderInfo.roles,
    createdBy: invoiceDetails?.orderInfo.createdByUser,
    createdDate: invoiceDetails?.orderInfo.orderDate,
    fromPickup: state.fromPickup ?? true,
  };
  const fromPickupBreadCrumbs = {
    steps: PickupCrumbSteps,
    activeItem: 'invoice_name',
    type: OrderProcessingMasterData.PICKUP,
    stateProp: OrderInfoDetails,
    orderNumber: state.orderNumber,
    invoiceName: invoiceDetails?.invoiceInfo.invoiceName,
  };

  const fromOrderedItemListBreadCrumbs = {
    steps: InvocieCrumbSteps,
    activeItem: 'invoice_name',
    type: OrderProcessingMasterData.INVOICE,
    stateProp: OrderInfoDetails,
    orderNumber: state.orderNumber,
    invoiceName: invoiceDetails?.invoiceInfo.invoiceName,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      {
        state.fromPickup ? (
          <SideNav active={7} /> // 7 - Pick up module will be active in side nav
        ) : (
          <SideNav active={6} />
        ) // 6 - Invoice module will be active in side nav
      }
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        {state.fromPickup ? (
          // Using Pick up breadcrumbs if its from Pick up list
          <OrderListBreadcrumbs orderInfo={fromPickupBreadCrumbs} />
        ) : (
          // Or if its from Ordered Item list - Invoice
          <OrderListBreadcrumbs orderInfo={fromOrderedItemListBreadCrumbs} />
        )}
        <div className="p-3">
          {/* Title section */}
          <div className="sticky top-0 z-20 bg-white border-b">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-gray-700">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="p-1"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <h2 className="text-lg font-semibold text-gray-900">
                  {invoiceDetails?.invoiceInfo.invoiceName}
                </h2>
              </div>
              <div className="flex items-center">
                {!state.fromPickup ? (
                  <>
                    {/* From Invoice - details */}
                    {invoiceDetails?.invoiceInfo.status == 1 && ( //Invoiced and mark ready for pick
                      <button
                        type="submit"
                        onClick={() =>
                          showConfirmationToast(
                            showRefund === true
                              ? `Please refund $${Math.abs(finalPriceToBePaid).toFixed(2)} to the customer before proceeding for pick up.`
                              : `Please collect $${finalPriceToBePaid.toFixed(2)} from the customer before proceeding for pick up.`,
                            showRefund === true
                              ? 'Confirm Refund'
                              : 'Confirm Final Payment',
                            'info',
                            handleConfirmPayment,
                            showRefund === true
                              ? 'Confirm Refund'
                              : 'Confirm Payment',
                            undefined,
                            'Cancel',
                          )
                        }
                        className="w-full mr-2 px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        <span>Ready For Pickup</span>
                      </button>
                    )}
                    {invoiceDetails?.invoiceInfo.status == 2 && ( // Marked ready for pickup - invoice paid
                      <span className="inline-block w-full bg-[#DEE7FF] mr-2 p-2 border border-[#5279E4] text-[#5279E4] text-center font-semibold rounded-md">
                        Paid
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="text-[#5279E4] ml-1"
                        />
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {/* From Pickup - details */}
                    {invoiceDetails?.invoiceInfo.status == 2 && ( // Paid invoice can be marked as Picked up
                      <button
                        type="submit"
                        onClick={() =>
                          showConfirmationToast(
                            'This action is final and cannot be undone. Please confirm the customer has collected their order.',
                            'Confirm Pickup',
                            'info',
                            handleConfirmPickedUp,
                            'Confirm Pickup',
                            undefined, // onCancel callback
                            'Cancel', // cancelButtonName
                          )
                        }
                        className="w-full mr-2 px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        <span>Mark as Picked up</span>
                      </button>
                    )}
                    {invoiceDetails?.invoiceInfo.status == 3 && ( // The order is picked up
                      <span className="inline-block w-full bg-[#CCEECC] mr-2 p-2 border border-[#33795F] text-[#33795F] text-center font-semibold rounded-md">
                        Picked up
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="ml-2 text-[#33795F]"
                        />
                      </span>
                    )}
                  </>
                )}

                {/* Invoice Download Icon */}
                <button
                  type="button"
                  onClick={handlePdfDownload}
                  className="text-gray-600 hover:text-gray-900 p-2 border border-[#D4D4D4] rounded hover:border-gray-400"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {/* Pickup success notification for marked up pickup success and
                     when invocie status is 3(picked up)  */}
          {showPickedUpNotification ||
            (invoiceDetails?.invoiceInfo.status == 3 && (
              <div className="mt-4">
                <div className="w-full bg-[#CCEECC] p-2 border border-[#33795F] text-[#33795F] text-start font-semibold rounded-md">
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-[#33795F]"
                    />
                    <span className="ml-2">Picked Up</span>
                  </div>
                  <div className="text-[#555F71] font-semibold text-[12px] mt-1">
                    {invoiceDetails?.invoiceInfo.invoiceName} picked up on{' '}
                    {invoiceDetails?.invoiceInfo.modifiedAt}
                  </div>
                </div>
              </div>
            ))}
          {/* This will be displayed When order is paid and ready for pickup */}
          {invoiceDetails?.invoiceInfo.status == 2 &&
            !showPickedUpNotification && (
              <div className="mt-4">
                <span className="inline-block w-full bg-[#DEE7FF] p-2 border border-[#5279E4] text-[#5279E4] text-start font-semibold rounded-md">
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="text-[#5279E4]"
                  />
                  <span className="ml-2">
                    This order is paid for and is ready for pickup.
                  </span>
                </span>
              </div>
            )}
          {/* Order details card */}
          {state && (
            <OrderDetailsCard
              orderDetailsCardInfo={{
                orderNumber: state.orderNumber,
                createdByUser: invoiceDetails?.invoiceInfo?.createdBy ?? '',
                roles: state.roles,
                customerName: state.customerName,
                orderDate: invoiceDetails?.invoiceInfo?.invoiceDate ?? '',
                customerEmail: state.customerEmail,
                customerPhone: state.customerPhone,
              }}
              invoiceBy={true}
            />
          )}
          {/* Accordion content */}
          {invoiceDetails?.orderInfo?.species.map((species, index) => {
            // Check if there is any check-in data
            const hasCheckInData =
              species.checkIn.huntingLicenceId ||
              species.checkIn.permitNumber ||
              species.checkIn.citesNumber ||
              species.checkIn.conformationNumber ||
              species.checkIn.feet ||
              species.checkIn.weight ||
              species.checkIn.notes;

            return (
              <div key={species.speciesId}>
                <div className="border rounded-md shadow-sm mb-3 mt-4">
                  <button
                    type="button"
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
                  >
                    <span>
                      {species.speciesTag}
                      {species && species.checkIn.inches && (
                        <>
                          {''} ({species.checkIn.feet} {Length.FEET}{' '}
                          {species.checkIn.inches} {Length.INCHES})
                        </>
                      )}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowNotesModal(true)}
                        type="button"
                        className="text-primary text-sm px-2 font-bold whitespace-nowrap"
                      >
                        View notes
                      </button>
                      <FontAwesomeIcon
                        className="text-[#000A16]"
                        icon={
                          openAccordions.includes(index)
                            ? faChevronUp
                            : faChevronDown
                        }
                      />
                    </div>
                    {showNotesModal && (
                      <OrderNotesModal
                        speciesName={species?.speciesTag ?? ''}
                        onClose={() => setShowNotesModal(false)}
                        checkInNotes={
                          species?.checkIn?.notes && species.checkIn.notes !== ''
                            ? species.checkIn.notes
                            : errorMessage.NO_NOTES_FOUND
                        }
                        AdditionalNotes={
                          species?.notes != ''
                            ? (species?.notes ?? '')
                            : errorMessage.NO_NOTES_FOUND
                        }
                        ServiceNotes={species.services.map(
                          (service): OrderServiceNotes => ({
                            orderServiceId: service.serviceId,
                            serviceName: service.serviceName ?? '',
                            note: service.serviceProcessNotes
                              ?.map((ServiceNotes) => ServiceNotes.note)
                              .join(', '),
                          }),
                        )}
                      />
                    )}
                  </button>
                  {openAccordions.includes(index) && (
                    <>
                      {/* Check-in Section */}
                      <div className="p-3">
                        <h3 className="text-lg font-semibold ml-2 mb-2">
                          Check-In
                        </h3>
                        {hasCheckInData ? (
                          <div className="mt-4 p-3 rounded-lg shadow-md bg-quaternary font-medium">
                            {/* Species State and License Number */}
                            {species.checkIn.huntingLicenceId && (
                              <div className="bg-white p-4 rounded-md border mb-2">
                                <div className="flex w-full justify-start">
                                  <div className="w-1/2">
                                    <span className="block mb-2 text-[#000A1666] font-medium">
                                      Species State
                                    </span>
                                    <span>{species.checkIn.stateName}</span>
                                  </div>
                                  <div className="w-1/2 ">
                                    <span className="block mb-2 text-[#000A1666] font-medium">
                                      License Number
                                    </span>
                                    <div className="flex justify-between">
                                      <span>{species.checkIn.licenseNumber}</span>
                                      <span>
                                        Expiry:{' '}
                                        {formatDate(
                                          species.checkIn.licenseExpiryDate,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* Permit, CITES and Confirmation Number */}
                            {(species.checkIn.permitNumber ||
                              species.checkIn.citesNumber ||
                              species.checkIn.conformationNumber) && (
                                <div className="bg-white p-4 rounded-md border mb-2">
                                  <div className="flex w-full justify-start">
                                    {species.checkIn.permitNumber && (
                                      <div className="w-1/2">
                                        <span className="block mb-2 text-[#000A1666] font-medium">
                                          Permit Number
                                        </span>
                                        <span>{species.checkIn.permitNumber}</span>
                                      </div>
                                    )}
                                    {species.checkIn.citesNumber && (
                                      <div className="w-1/2">
                                        <span className="block mb-2 text-[#000A1666] font-medium">
                                          CITES Number
                                        </span>
                                        <span>{species.checkIn.citesNumber}</span>
                                      </div>
                                    )}
                                  </div>
                                  {species.checkIn.conformationNumber && (
                                    <div className="w-full mt-3">
                                      <span className="block mb-2 text-[#000A1666] font-medium">
                                        Confirmation Number
                                      </span>
                                      <span>
                                        {species.checkIn.conformationNumber}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            {/* Length and Weight */}
                            {(species.checkIn.feet || species.checkIn.weight) && (
                              <div className="bg-white p-4 rounded-md border mb-2">
                                <div className="flex w-full justify-start">
                                  {species.checkIn.feet && (
                                    <div className="w-1/2">
                                      <span className="block mb-2 text-[#000A1666] font-medium">
                                        Length
                                      </span>
                                      <span>
                                        {species.checkIn.feet} Feet{' '}
                                        {species.checkIn.inches} Inches
                                      </span>
                                    </div>
                                  )}
                                  {species.checkIn.weight && (
                                    <div className="w-1/2">
                                      <span className="block mb-2 text-[#000A1666] font-medium">
                                        Weight
                                      </span>
                                      <span>{species.checkIn.weight}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {species?.checkIn?.notes && (
                              <div className="bg-white p-4 rounded-md border mb-2 mt-4">
                                <h4 className="font-medium mb-2">
                                  Check - in notes:
                                </h4>
                                <div className="space-y-2">
                                  <span className="text-[#43474E]">
                                    {species.checkIn.notes}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center text-gray-500 py-4">
                            No check-in data available for the species
                          </div>
                        )}
                      </div>

                      {/* Services */}
                      {species.services.length != 0 && (
                        <div className="mt-3">
                          <h3 className="text-lg font-semibold ml-2 mb-2">
                            Services
                          </h3>
                          {species.services.map((service) => (
                            <div
                              key={service.serviceId}
                              className="p-4 rounded-md border bg-tertiary mb-4"
                            >
                              <div className="flex gap-4 items-center">
                                <img
                                  src={species.speciesImageUrl}
                                  alt={species.speciesTag}
                                  className="w-16 h-16 object-cover rounded-md"
                                />
                                <div className="flex-1">
                                  <div className="flex justify-between items-start">
                                    <p className="text-gray-900 text-lg font-semibold">
                                      {service.serviceName}
                                    </p>
                                    {/* Hide the price for pick up screen */}
                                    {!state.fromPickup && (
                                      <p className="text-gray-900 font-semibold text-lg whitespace-nowrap">
                                        ${service.price.toFixed(2)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {/* Hide the price for pick up screen */}
                              {service.depositAmount != 0 &&
                                !state.fromPickup && (
                                  <div className="bg-white p-2 mt-2 rounded-md border flex justify-between items-center text-sm text-gray-600">
                                    <span className="font-semibold">
                                      Deposit Collected
                                    </span>
                                    <span className="text-gray-800 font-semibold">
                                      ${service.depositAmount}
                                    </span>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Products */}
                      {species?.products?.length != 0 && (
                        <div className="mt-4 rounded-lg">
                          <h3 className="text-lg font-semibold mt-3 ml-2">
                            Products and Packaging
                          </h3>
                          {species?.products?.map((product) => {
                            // Calculate total price
                            const productsTotalPrice = (
                              product.price +
                              product.addons?.reduce(
                                (sum, addon) => sum + (addon.totalPrice || 0),
                                0,
                              ) +
                              product.packing?.reduce(
                                (sum, pack) => sum + (pack.price || 0),
                                0,
                              )
                            ).toFixed(2);
                            return (
                              <div
                                key={product.productId}
                                className="bg-tertiary p-2 rounded-lg mt-3"
                              >
                                <div className="p-4">
                                  <div className="flex justify-between items-baseline mb-1">
                                    <h4 className="font-semibold text-lg">
                                      {product?.name}
                                      {product.fatPercent > 0 &&
                                        `, ${product.fatPercent}% Fat`}
                                    </h4>
                                    {/* Hide the price for pick up screen */}
                                    {!state.fromPickup && (
                                      <span className="text-gray-900 font-semibold text-xl">
                                        ${productsTotalPrice}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex justify-between items-baseline">
                                    <p className="text-gray-600">
                                      Total Quantity: {product.finalQuantity}
                                    </p>
                                    {/* Hide the price for pick up screen */}
                                    {!state.fromPickup && (
                                      <span className="text-[#43474E]">
                                        ${product.price.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Add-ons */}
                                {product.addons?.length > 0 && (
                                  <div className="bg-white p-4 rounded-md border mb-4">
                                    <h4 className="font-medium mb-2">
                                      Add-ons:{' '}
                                    </h4>
                                    <div className="space-y-2">
                                      {product.addons.map((addOnId, index) => {
                                        return (
                                          <div
                                            key={index}
                                            className="flex justify-between"
                                          >
                                            <span className="w-1/2">
                                              {addOnId?.name}
                                            </span>
                                            {/* Hide the price for pick up screen */}
                                            {!state.fromPickup && (
                                              <span className="w-1/2 text-right">
                                                Price: $
                                                {addOnId?.totalPrice?.toFixed(
                                                  2,
                                                ) ?? '0.00'}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {/* Packaging */}
                                {product.packing?.length > 0 && (
                                  <div className="bg-white p-4 rounded-md border mb-4">
                                    <h4 className="font-medium mb-2">
                                      Packaging:
                                    </h4>
                                    <div className="space-y-2">
                                      {product.packing.map((pack) => (
                                        <div
                                          key={pack.packageTypeId}
                                          className="flex justify-between"
                                        >
                                          <span
                                            className={
                                              product.packing.length === 1
                                                ? 'w-1/3'
                                                : 'w-1/4'
                                            }
                                          >
                                            {pack.packageType}
                                          </span>
                                          <span
                                            className={
                                              product.packing.length === 1
                                                ? 'w-1/3 text-center'
                                                : 'w-1/4'
                                            }
                                          >
                                            <span className="text-[#43474E]">
                                              Size:
                                            </span>{' '}
                                            {pack.size} {WeightType.LBS}
                                          </span>
                                          {product.packing.length > 1 && (
                                            <span className="w-1/4">
                                              <span className="text-[#43474E]">
                                                Pkg Split:
                                              </span>{' '}
                                              {pack.percentage}%
                                            </span>
                                          )}
                                          <span
                                            className={
                                              product.packing.length === 1
                                                ? 'w-1/3 text-right'
                                                : 'w-1/4 text-right'
                                            }
                                          >
                                            {/* Hide the price for pick up screen */}
                                            {!state.fromPickup && (
                                              <>
                                                <span className="w-1/4 text-[#43474E]">
                                                  Price:{' '}
                                                </span>{' '}
                                                ${pack.price?.toFixed(2)}
                                              </>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Packaging Notes */}
                                {product.note && (
                                  <div className="bg-white p-4 rounded-md border mb-4">
                                    <h4 className="font-medium mb-2">
                                      Packaging Notes:
                                    </h4>
                                    <span className="text-[#43474E]">
                                      {product.note}
                                    </span>
                                  </div>
                                )}

                                {/* Product Notes */}
                                {product.productProcessNotes?.[0]?.note && (
                                  <div className="bg-white p-4 rounded-md border mb-4">
                                    <h4 className="font-medium mb-2">
                                      Product Notes:
                                    </h4>
                                    <span className="text-[#43474E]">
                                      {product.productProcessNotes[0].note}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {/* Hide the total breakage for pick up screen */}
          {!state.fromPickup && (
            <>
              <div className="mt-3 border border-b-[#00000033]"></div>
              {/* Breakage */}
              <div className="flex justify-between mt-3 font-semibold">
                <span>Sub Total</span>
                <span className=" text-xl">
                  ${invoiceDetails?.invoiceInfo.subTotalAmount?.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between mt-3 font-semibold">
                <span>Tax</span>
                <span className=" text-xl">
                  ${invoiceDetails?.invoiceInfo.taxAmount?.toFixed(2)}
                </span>
              </div>

              <div className="mt-3 border border-b-[#00000033]"></div>

              <div className="flex justify-between mt-3 font-semibold">
                <span>Total</span>
                <span className="text-xl">
                  ${invoiceDetails?.invoiceInfo.totalAmount?.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center bg-white mt-2 border-b pb-2">
                <span className="font-semibold ">Storage fees</span>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 transform -translate-y-1/2 font-semibold">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="^\d+(\.\d{0,2})?$"
                    value={
                      extraStorageFees ??
                      invoiceDetails?.invoiceInfo.storageCost
                    }
                    readOnly={invoiceDetails?.invoiceInfo.status === 2}
                    onChange={(e) => {
                      if (
                        /^\d*\.?\d{0,2}$/.test(e.target.value) ||
                        e.target.value === ''
                      ) {
                        setExtraStorageFees(Number(e.target.value));
                      }
                    }}
                    className="pl-5 pr-2 py-2 border rounded-md font-semibold w-full text-right focus:outline-primary text-gray-800"
                  />
                </div>
              </div>
              {/* Discount */}
              <div className="flex justify-between items-center bg-white mt-2 border-b pb-2">
                <span className="font-semibold ">Discount</span>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 transform -translate-y-1/2 font-semibold">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="^(0|[1-9]\d*)(\.\d*)?$"
                    value={
                      discount ?? invoiceDetails?.invoiceInfo.discountAmount
                    }
                    readOnly={invoiceDetails?.invoiceInfo.status == 2}
                    onChange={(e) => {
                      if (
                        /^\d*\.?\d{0,2}$/.test(e.target.value) ||
                        e.target.value === ''
                      ) {
                        setDiscount(Number(e.target.value));
                      }
                    }}
                    className="pl-5 pr-2 py-2 border rounded-md font-semibold w-full text-right focus:outline-primary bg-[#FFDBDB]"
                  />
                </div>
              </div>

              {/* Advance Payment */}
              <div className="rounded-md shadow-sm p-2 bg-tertiary mt-3">
                <div className="flex justify-between items-center bg-tertiary ">
                  <span className="text-[#1C1B1F] font-medium">
                    Total Advance Payment Made:
                  </span>
                  <span className="font-semibold text-[#43474E] py-2 px-3 text-lg min-w-[100px] inline-block text-center">
                    ${invoiceDetails?.orderInfo.advancePayment || 0}
                  </span>
                </div>
              </div>
              {/* Deposit */}
              <div className="rounded-md shadow-sm p-2 bg-tertiary mt-3">
                <div className="flex justify-between items-center bg-tertiary ">
                  <span className="text-[#1C1B1F] font-medium">
                    Total Deposits Made Against This Invoice:
                  </span>
                  <span className="font-semibold text-[#43474E] py-2 px-3 text-lg min-w-[100px] inline-block text-center">
                    ${totalDeposits || 0}
                  </span>
                </div>
              </div>
              <div
                className="flex justify-between mt-3 py-2 px-1 font-semibold"
                style={{
                  backgroundColor:
                    finalPriceToBePaid < 0 ? '#FFDBDB' : '#CCEECC',
                }}
              >
                <span>
                  {finalPriceToBePaid < 0
                    ? 'Refund the customer'
                    : 'Final price to be paid by the customer'}
                </span>
                <span className="border border-spacing-px bg-white pt-[4px] pr-[24px] pl-[24px] pb-[4px] text-xl">
                  ${Math.abs(finalPriceToBePaid).toFixed(2)}{' '}
                  {/* Math.abs to remove '-(minus)' for negative value */}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};  